import type { TOAST_TYPE, ToastProps } from './types';

export default class ToastManager {
  private static instance: ToastManager;
  private toasts: ToastProps[] = [];
  private readonly listeners = new Set<(toasts: ToastProps[]) => void>();
  private static readonly MAX_TOASTS = 3;

  private constructor() {}

  public static getInstance(): ToastManager {
    if (!ToastManager.instance) {
      ToastManager.instance = new ToastManager();
    }
    return ToastManager.instance;
  }

  public addToast(toast: ToastProps): void {
    const id = toast.id ?? Math.random().toString(36).substring(2, 9);
    const newToast: ToastProps = {
      duration: 5000,
      type: 'info' as TOAST_TYPE,
      autoClose: true,
      ...toast,
      id,
    };

    if (this.toasts.some((t) => t.description === toast.description)) {
      return;
    }

    this.toasts = [...this.toasts, newToast];

    // If we have more than MAX_TOASTS, remove the oldest one(s)
    if (this.toasts.length > ToastManager.MAX_TOASTS) {
      this.toasts = this.toasts.slice(-ToastManager.MAX_TOASTS);
    }

    this.notifyListeners();

    if (newToast.duration !== Number.POSITIVE_INFINITY && newToast.autoClose) {
      setTimeout(() => {
        this.removeToast(id);
      }, newToast.duration);
    }
  }

  public removeToast(id: string): void {
    this.toasts = this.toasts.filter((toast) => toast.id !== id);
    this.notifyListeners();
  }

  public getToasts(): ToastProps[] {
    return this.toasts;
  }

  public subscribe(listener: (toasts: ToastProps[]) => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private notifyListeners(): void {
    this.listeners.forEach((listener) => listener(this.toasts));
  }
}

export const toastManager = ToastManager.getInstance();
